import React, { useState } from "react";
import {
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Collapse,
  Divider,
  IconButton,
} from "@mui/material";
import {
  Close,
  ExpandMore,
  ExpandLess,
  FilterList,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";

/**
 * FilterPanel — fully dynamic, reusable filter drawer
 *
 * Props:
 *  fields   Array<FieldConfig>  — field definitions (see below)
 *  onApply  (values: object) => void
 *  onClear  () => void
 *  isOpen   boolean
 *  onClose  () => void
 *
 * FieldConfig:
 *  {
 *    key:     string           — unique key, used in values object
 *    label:   string           — display label
 *    type:    'text'           — plain text input
 *           | 'date'           — date picker (HTML date input)
 *           | 'daterange'      — two date inputs (from / to), produces key_from & key_to
 *           | 'select'         — single-select dropdown
 *           | 'multiselect'    — collapsible checkbox list
 *    options: string[]         — required for select / multiselect
 *  }
 *
 * Usage Example:
 *  const fields = [
 *    { key: 'probationDate', label: 'Probation Date', type: 'date' },
 *    { key: 'status', label: 'Status', type: 'select', options: ['Active', 'Inactive'] },
 *    { key: 'departments', label: 'Departments', type: 'multiselect', options: ['Eng', 'HR'] },
 *  ];
 *
 *  <FilterPanel
 *    fields={fields}
 *    isOpen={showFilter}
 *    onClose={() => setShowFilter(false)}
 *    onApply={(vals) => console.log(vals)}
 *    onClear={() => console.log('cleared')}
 *  />
 */

const ACCENT = "#c0392b";

export default function FilterPanel({ fields = [], onApply, onClear, isOpen, onClose }) {
  const [values, setValues] = useState({});
  const [openSections, setOpenSections] = useState({});

  const setValue = (key, val) => setValues((prev) => ({ ...prev, [key]: val }));

  const toggleSection = (key) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleApply = () => {
    console.log("Applying filters with values:", values);
    onApply && onApply(values);
    onClose && onClose();
  };

  const handleClear = () => {
    setValues({});
    setOpenSections({});
    onClear && onClear();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.4)",
          zIndex: 1200,
        }}
      />

      {/* Drawer */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          height: "100vh",
          width: 320,
          backgroundColor: "#fff",
          zIndex: 1300,
          display: "flex",
          flexDirection: "column",
          boxShadow: "-4px 0 24px rgba(0,0,0,0.15)",
        }}
      >
        {/* Header */}
        <div
          className="d-flex align-items-center justify-content-between px-3 py-3"
          style={{ borderBottom: "1px solid #e0e0e0" }}
        >
          <span style={{ fontSize: 17, fontWeight: 600 }}>Filters</span>
          <IconButton size="small" onClick={onClose} aria-label="Close filters">
            <Close fontSize="small" />
          </IconButton>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: "auto" }} className="py-2">
          {fields.map((field) => (
            <FieldRenderer
              key={field.key}
              field={field}
              values={values}
              setValue={setValue}
              openSections={openSections}
              toggleSection={toggleSection}
            />
          ))}
        </div>

        {/* Footer */}
        <div
          className="d-flex gap-2 px-3 py-3"
          style={{ borderTop: "1px solid #e0e0e0" }}
        >
          <button
            className="btn btn-danger flex-fill fw-500"
            style={{ backgroundColor: ACCENT, borderColor: ACCENT }}
            onClick={handleApply}
          >
            Apply
          </button>
          <button
            className="btn btn-secondary flex-fill"
            onClick={handleClear}
          >
            Clear
          </button>
        </div>
      </div>
    </>
  );
}

function FieldRenderer({ field, values, setValue, openSections, toggleSection }) {
  switch (field.type) {
    case "text":
      return <TextFieldRow field={field} values={values} setValue={setValue} />;
    case "date":
      return <DateFieldRow field={field} values={values} setValue={setValue} />;
    case "daterange":
      return <DateRangeRow field={field} values={values} setValue={setValue} />;
    case "select":
      return (
        <CollapsibleRow
          field={field}
          isOpen={openSections[field.key]}
          onToggle={() => toggleSection(field.key)}
        >
          <SelectContent field={field} values={values} setValue={setValue} />
        </CollapsibleRow>
      );
    case "multiselect":
      return (
        <CollapsibleRow
          field={field}
          isOpen={openSections[field.key]}
          onToggle={() => toggleSection(field.key)}
        >
          <MultiSelectContent field={field} values={values} setValue={setValue} />
        </CollapsibleRow>
      );
    default:
      return null;
  }
}

function TextFieldRow({ field, values, setValue }) {
  return (
    <div className="px-3 py-2">
      <TextField
        fullWidth
        size="small"
        label={field.label}
        value={values[field.key] || ""}
        onChange={(e) => setValue(field.key, e.target.value)}
        variant="outlined"
      />
    </div>
  );
}

function DateFieldRow({ field, values, setValue }) {
  return (
    <div className="px-3 py-2">
      <DatePicker
        label={field.label}
        value={values[field.key] || null}
        onChange={(newVal) => setValue(field.key, newVal)}
        format="dd MMM yyyy"
        slotProps={{
          textField: {
            size: "small",
            fullWidth: true,
            sx: {
              backgroundColor: "white",
              "& .MuiInputBase-root": {
                height: "40px",
              },
              "& .MuiInputBase-input": {
                padding: "10px 12px",
              },
            },
          },
        }}
      />
    </div>
  );
}

function DateRangeRow({ field, values, setValue }) {
  return (
    <div className="px-3 py-2">
      <div
        className="mb-2"
        style={{ fontSize: 12, color: "#666", fontWeight: 500, textTransform: "uppercase" }}
      >
        {field.label}
      </div>

      <div className="d-flex gap-2">
        <DatePicker
          label="From"
          value={values[field.key + "_from"] || null}
          onChange={(newVal) => setValue(field.key + "_from", newVal)}
          format="dd MMM yyyy"
          slotProps={{
            textField: {
              size: "small",
              fullWidth: true,
              sx: {
                backgroundColor: "white",
                "& .MuiInputBase-root": {
                  height: "40px",
                },
                "& .MuiInputBase-input": {
                  padding: "10px 12px",
                },
              },
            },
          }}
        />

        <DatePicker
          label="To"
          value={values[field.key + "_to"] || null}
          onChange={(newVal) => setValue(field.key + "_to", newVal)}
          format="dd MMM yyyy"
          minDate={values[field.key + "_from"] || undefined}
          slotProps={{
            textField: {
              size: "small",
              fullWidth: true,
              sx: {
                backgroundColor: "white",
                "& .MuiInputBase-root": {
                  height: "40px",
                },
                "& .MuiInputBase-input": {
                  padding: "10px 12px",
                },
              },
            },
          }}
        />
      </div>
    </div>
  );
}

function CollapsibleRow({ field, isOpen, onToggle, children }) {
  return (
    <div style={{ borderTop: "1px solid #f0f0f0" }}>
      <div
        role="button"
        tabIndex={0}
        className="d-flex align-items-center justify-content-between px-3 py-2"
        style={{ cursor: "pointer", userSelect: "none" }}
        onClick={onToggle}
        onKeyDown={(e) => e.key === "Enter" && onToggle()}
        aria-expanded={isOpen}
      >
        <span style={{ fontSize: 14, fontWeight: 500 }}>{field.label}</span>
        {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
      </div>
      <Collapse in={isOpen} unmountOnExit>
        {children}
      </Collapse>
    </div>
  );
}

function SelectContent({ field, values, setValue }) {
  return (
    <div className="px-3 pb-2">
      <FormControl fullWidth size="small">
        <InputLabel>Select {field.label}</InputLabel>
        <Select
          value={values[field.key] || ""}
          label={`Select ${field.label}`}
          onChange={(e) => setValue(field.key, e.target.value)}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {field.options.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
}

function MultiSelectContent({ field, values, setValue }) {
  const selected = values[field.key] || [];

  const handleChange = (opt, checked) => {
    const updated = checked
      ? [...selected, opt]
      : selected.filter((v) => v !== opt);
    setValue(field.key, updated);
  };

  return (
    <div className="px-3 pb-2">
      <FormGroup>
        {field.options.map((opt) => (
          <FormControlLabel
            key={opt.value}
            control={
              <Checkbox
                size="small"
                checked={selected.includes(opt.value)}
                onChange={(e) => handleChange(opt.value, e.target.checked)}
                sx={{
                  "&.Mui-checked": { color: "#c0392b" },
                  padding: "4px 8px",
                }}
              />
            }
            label={<span style={{ fontSize: 14 }}>{opt.label}</span>}
          />
        ))}
      </FormGroup>
    </div>
  );
}

/**
 * FilterToggleButton — companion button to open the filter panel
 *
 * Usage: <FilterToggleButton onClick={() => setShowFilter(true)} />
 */
export function FilterToggleButton({ onClick, label = "Filters" }) {
  return (
    <button
      className="btn d-flex align-items-center gap-2"
      style={{
        backgroundColor: "#c0392b",
        color: "#fff",
        borderColor: "#c0392b",
        fontWeight: 500,
      }}
      onClick={onClick}
    >
      <FilterList style={{ fontSize: 18 }} />
      {label}
    </button>
  );
}